import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, XCircle, Loader2, RefreshCw, ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import { useToast } from '../hooks/useToast';
import {
    fetchCreditSaleApprovals,
    approveCreditSale,
    rejectCreditSale,
    currencyCodeToSymbolMap,
} from '../apiHelper';

const statusBadge = (status) => {
    const base = 'inline-flex px-2 py-0.5 rounded text-xs font-semibold';
    if (status === 'pending') return `${base} bg-amber-100 text-amber-800`;
    if (status === 'approved') return `${base} bg-green-100 text-green-800`;
    if (status === 'rejected') return `${base} bg-red-100 text-red-800`;
    return `${base} bg-gray-100 text-gray-700`;
};

const CreditSaleApprovals = ({ storeId = null }) => {
    const { showToast } = useToast();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [notesModal, setNotesModal] = useState(null); // { type: 'approve'|'reject', row }
    const [notes, setNotes] = useState('');
    const [expandedRows, setExpandedRows] = useState({}); // Track expanded rows by approval id

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchCreditSaleApprovals({
                store_id: storeId || undefined,
                status: statusFilter || undefined,
            });
            setItems(res?.items || []);
        } catch (err) {
            console.error(err);
            showToast(err?.response?.data?.detail || 'Failed to load approvals', 'error');
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [storeId, statusFilter, showToast]);

    useEffect(() => {
        load();
    }, [load]);

    const toggleRowExpansion = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const openAction = (type, row) => {
        setNotes('');
        setNotesModal({ type, row });
    };

    const confirmAction = async () => {
        if (!notesModal) return;
        const { type, row } = notesModal;
        setActionLoadingId(row.orders_id);
        try {
            if (type === 'approve') {
                await approveCreditSale(row.orders_id, notes || null);
                showToast('Credit sale approved and finalized', 'success');
            } else {
                await rejectCreditSale(row.orders_id, notes || null);
                showToast('Credit sale rejected', 'success');
            }
            setNotesModal(null);
            await load();
        } catch (err) {
            showToast(err?.response?.data?.detail || 'Action failed', 'error');
        } finally {
            setActionLoadingId(null);
        }
    };

    const symbol = (code) => currencyCodeToSymbolMap[code] || code || '';

    return (
        <div className="p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div className="flex items-center gap-2">
                    <ClipboardList className="text-gmail-blue" size={24} />
                    <h1 className="text-xl font-bold text-text-primary">Credit sale approvals</h1>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="">All</option>
                    </select>
                    <Button variant="secondary" size="sm" onClick={load} className="flex items-center gap-2">
                        <RefreshCw size={16} /> Refresh
                    </Button>
                </div>
            </div>

            <Card className="overflow-hidden">
                {loading ? (
                    <div className="flex justify-center items-center py-16 text-text-secondary">
                        <Loader2 className="animate-spin mr-2" size={22} /> Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-16 text-text-secondary text-sm">
                        No credit sales in this filter.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-text-primary">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50">
                                    <th className="px-2 py-2 w-10"></th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">When</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Ref</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Customer</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Cashier</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Method</th>
                                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Credit</th>
                                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Total</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => {
                                    const isExpanded = !!expandedRows[row.creditsaleapproval_id];
                                    return (
                                        <React.Fragment key={row.creditsaleapproval_id}>
                                            <tr className="border-b border-gray-100 hover:bg-gray-50">
                                                <td className="px-2 py-2 text-center">
                                                    <button
                                                        onClick={() => toggleRowExpansion(row.creditsaleapproval_id)}
                                                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                                                        aria-label="Toggle details"
                                                    >
                                                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                    </button>
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                                                    {row.created_on ? new Date(row.created_on).toLocaleString() : '—'}
                                                </td>
                                                <td className="px-3 py-2 font-mono text-xs">{row.idempotency_key}</td>
                                                <td className="px-3 py-2">{row.customer_name || row.customer_id || '—'}</td>
                                                <td className="px-3 py-2">{row.requester_name || row.requested_by}</td>
                                                <td className="px-3 py-2 capitalize">{row.payment_method || '—'}</td>
                                                <td className="px-3 py-2 text-right">
                                                    {row.credit_amount != null
                                                        ? `${symbol(row.currency)}${Number(row.credit_amount).toFixed(2)}`
                                                        : '—'}
                                                </td>
                                                <td className="px-3 py-2 text-right font-medium">
                                                    {row.total_amount != null
                                                        ? `${symbol(row.currency)}${Number(row.total_amount).toFixed(2)}`
                                                        : '—'}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className={statusBadge(row.status)}>{row.status}</span>
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    {row.status === 'pending' ? (
                                                        <div className="flex justify-end gap-2">
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                className="flex items-center gap-1"
                                                                disabled={actionLoadingId === row.orders_id}
                                                                onClick={() => openAction('approve', row)}
                                                            >
                                                                {actionLoadingId === row.orders_id ? (
                                                                    <Loader2 size={14} className="animate-spin" />
                                                                ) : (
                                                                    <CheckCircle size={14} />
                                                                )}
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                variant="secondary"
                                                                size="sm"
                                                                className="flex items-center gap-1 text-red-600"
                                                                disabled={actionLoadingId === row.orders_id}
                                                                onClick={() => openAction('reject', row)}
                                                            >
                                                                <XCircle size={14} /> Reject
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">—</span>
                                                    )}
                                                </td>
                                            </tr>

                                            {/* Expanded details row showing items, breakdown, and notes */}
                                            {isExpanded && (
                                                <tr className="bg-gray-50 border-b border-gray-200">
                                                    <td colSpan="10" className="px-6 py-4">
                                                        <div className="text-xs space-y-3">
                                                            <div className="font-semibold text-gray-700 text-sm uppercase tracking-wider">
                                                                Order Line Items
                                                            </div>
                                                            <div className="bg-white rounded border border-gray-200 overflow-hidden">
                                                                <table className="min-w-full text-xs">
                                                                    <thead className="bg-gray-100 text-gray-600 border-b border-gray-200">
                                                                        <tr>
                                                                            <th className="px-3 py-1.5 text-left">Product Name</th>
                                                                            <th className="px-3 py-1.5 text-center">Quantity</th>
                                                                            <th className="px-3 py-1.5 text-right">Unit Price</th>
                                                                            <th className="px-3 py-1.5 text-right">Total</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {row.items?.map((item, idx) => (
                                                                            <tr key={idx} className="border-b border-gray-100 last:border-b-0">
                                                                                <td className="px-3 py-1.5 font-medium">{item.productname}</td>
                                                                                <td className="px-3 py-1.5 text-center">{item.quantity}</td>
                                                                                <td className="px-3 py-1.5 text-right">{symbol(row.currency)}{Number(item.price).toFixed(2)}</td>
                                                                                <td className="px-3 py-1.5 text-right font-semibold">
                                                                                    {symbol(row.currency)}{Number(item.quantity * item.price).toFixed(2)}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>

                                                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-gray-600 mt-2">
                                                                <div><span className="font-semibold">Subtotal:</span> {symbol(row.currency)}{Number(row.subtotal || 0).toFixed(2)}</div>
                                                                <div><span className="font-semibold">Total Tax:</span> {symbol(row.currency)}{Number(row.totaltax || 0).toFixed(2)}</div>
                                                                <div><span className="font-semibold">Total Discount:</span> {symbol(row.currency)}{Number(row.totaldiscount || 0).toFixed(2)}</div>
                                                                {row.notes && <div><span className="font-semibold">Notes:</span> {row.notes}</div>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <Modal
                isOpen={!!notesModal}
                onClose={() => setNotesModal(null)}
                title={notesModal?.type === 'approve' ? 'Approve credit sale' : 'Reject credit sale'}
                size="md"
            >
                <div className="p-4 space-y-4">
                    {notesModal && (
                        <p className="text-sm text-text-secondary">
                            Order <span className="font-mono">{notesModal.row.idempotency_key}</span>
                            {notesModal.row.customer_name ? ` · ${notesModal.row.customer_name}` : ''}
                            {notesModal.row.total_amount != null
                                ? ` · ${symbol(notesModal.row.currency)}${Number(notesModal.row.total_amount).toFixed(2)}`
                                : ''}
                        </p>
                    )}
                    <Input
                        label="Notes (optional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Reason or comment"
                    />
                    <div className="flex gap-3">
                        <Button
                            variant="primary"
                            className="flex-1"
                            onClick={confirmAction}
                            disabled={!!actionLoadingId}
                        >
                            {notesModal?.type === 'approve' ? 'Confirm approve' : 'Confirm reject'}
                        </Button>
                        <Button variant="secondary" className="flex-1" onClick={() => setNotesModal(null)}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CreditSaleApprovals;